import { BusinessIdea } from '../types/ai';
import { PreReleaseProject } from './preReleaseService';
import { JourneyMarket, JourneyPersona } from '../types/journey';
import { supabase } from './supabaseClient';

/**
 * AI-powered Pre-Release Project Generation Service
 * Generates contextual pre-release projects based on selected business ideas
 */
export class AIPreReleaseProjectService {
  // Use the same AI provider configuration as AIIdeaGeneratorService
  private static readonly AI_PROVIDER = 'deepseek';
  private static readonly EDGE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-openrouter-proxy`;
  private static readonly timeout = 30000;

  /**
   * Generate AI-powered pre-release project based on selected business idea
   */
  static async generatePreReleaseProject(
    selectedIdea: BusinessIdea,
    market: JourneyMarket,
    persona: JourneyPersona,
    language: string = 'en',
    projectId?: string
  ): Promise<Partial<PreReleaseProject>> {
    console.log('🎯 Starting AI pre-release project generation');
    console.log('💡 Idea:', selectedIdea.title);
    console.log('🏢 Market:', market);
    console.log('👤 Persona:', persona);
    console.log('🌐 Language:', language);
    
    try {
      const prompt = this.createPreReleaseProjectPrompt(selectedIdea, market, persona, language);
      console.log('📝 Prompt created, calling AI...');
      
      const response = await this.callAI(prompt);
      console.log('✅ AI response received, parsing...');
      
      const result = this.parsePreReleaseProject(response, selectedIdea);
      console.log('✅ Pre-release project generated successfully:', result.title);
      
      // Store language metadata in localStorage
      if (projectId) {
        try {
          localStorage.setItem(`content_language_prerelease_${projectId}`, language);
          console.log(`✅ Stored language metadata for pre-release project: ${projectId} (${language})`);
        } catch (error) {
          console.warn(`⚠️ Failed to store language metadata for project ${projectId}:`, error);
        }
      }
      
      return result;
    } catch (error) {
      console.error('❌ AI pre-release generation failed, using fallback:', error);
      console.log('🔄 Generating fallback pre-release project...');
      
      const fallback = this.getFallbackPreReleaseProject(selectedIdea, market, persona, language);
      console.log('✅ Fallback project generated:', fallback.title);
      
      // Store language metadata for fallback project too
      if (projectId) {
        try {
          localStorage.setItem(`content_language_prerelease_${projectId}`, language);
          console.log(`✅ Stored language metadata for fallback project: ${projectId} (${language})`);
        } catch (error) {
          console.warn(`⚠️ Failed to store language metadata for fallback project ${projectId}:`, error);
        }
      }
      
      return fallback;
    }
  }

  /**
   * Create AI prompt for pre-release project generation
   */
  private static createPreReleaseProjectPrompt(
    idea: BusinessIdea,
    market: JourneyMarket,
    persona: JourneyPersona,
    language: string = 'en'
  ): string {
    const randomSeed = Math.floor(Math.random() * 10000);
    const currentTime = new Date().toISOString();
    
    const languageInstruction = language !== 'en' 
      ? `\n\n🌐 CRITICAL LANGUAGE REQUIREMENT 🌐
GENERATE ALL CONTENT IN ${this.getLanguageName(language).toUpperCase()} LANGUAGE ONLY!
- title: must be in ${this.getLanguageName(language)}
- description: must be in ${this.getLanguageName(language)}
- concept_details: must be in ${this.getLanguageName(language)}
- key_features: ALL features must be in ${this.getLanguageName(language)}
- validation_goals: ALL goals must be in ${this.getLanguageName(language)}
- estimated_timeline: must be in ${this.getLanguageName(language)}
DO NOT use English. Use ${this.getLanguageName(language)} for ALL text content.`
      : '';

    return `You are a startup advisor creating a pre-release project for user testing. Session: ${randomSeed} at ${currentTime}${languageInstruction}

Business Idea Context:
- Title: ${idea.title}
- Problem: ${idea.problem}
- Solution: ${idea.solution}
- Target Customer: ${idea.targetCustomer || idea.targetAudience}
- Technical Feasibility: ${idea.technicalFeasibility}
- Skills Required: ${idea.skillsRequired.join(', ')}

User Context:
- Market: ${market}
- Persona: ${persona}
- Stage: Testing with Users (Step 3)

Generate a comprehensive pre-release project that helps validate this idea with real users BEFORE building the full solution.

Requirements:
1. Focus on testing the core value proposition with minimal development
2. Create assets that attract the right target customers
3. Design validation mechanisms to measure real demand
4. Include specific, actionable launch goals
5. Tailor messaging to the ${market} market and ${persona} persona

Format as JSON:
{
  "title": "Compelling project title that highlights the main benefit",
  "description": "2-3 sentence description explaining what this pre-release project will test and validate",
  "concept_details": "Detailed explanation of the concept being tested, including key assumptions to validate",
  "key_features": [
    "Feature 1: Core value proposition test",
    "Feature 2: User experience validation", 
    "Feature 3: Pricing/demand validation",
    "Feature 4: Market fit assessment",
    "Feature 5: Technical feasibility proof"
  ],
  "estimated_timeline": "Realistic timeline for this validation phase (e.g., '2-3 weeks')",
  "proposed_price": "Suggested early-bird pricing for validation (number only, e.g., 29)",
  "early_bird_discount": "Percentage discount for early supporters (number only, e.g., 50)",
  "project_category": "Category that fits the solution (e.g., 'Productivity', 'E-commerce', 'Developer Tools')",
  "validation_goals": [
    "Goal 1: Specific measurable outcome",
    "Goal 2: User behavior to validate",
    "Goal 3: Market response to measure",
    "Goal 4: Technical assumption to test"
  ]
}

Make it specific to the idea and actionable for immediate implementation. Focus on validation over perfection.

Return only the JSON object.`;
  }

  /**
   * Call AI API via Supabase Edge Function with error handling and retries
   */
  private static async callAI(prompt: string): Promise<string> {
    console.log('🔄 Calling AI via Edge Function...');
    console.log('📍 Edge Function URL:', this.EDGE_FUNCTION_URL);
    console.log('🤖 Provider:', this.AI_PROVIDER);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log('⏱️ Request timeout reached');
      controller.abort();
    }, this.timeout);

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session. Please sign in.');
      }

      const requestBody = {
        provider: this.AI_PROVIDER,
        prompt: prompt,
        operation: 'generate-prerelease-project',
        temperature: 0.8,
        maxTokens: 2048
      };

      console.log('📤 Sending request to Edge Function...');
      
      const response = await fetch(this.EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('📥 Received response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Edge Function Error Response:', errorText);
        throw new Error(`Edge Function request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Edge Function Response received, parsing...');

      if (!data.success || !data.data) {
        console.error('❌ Invalid response format:', data);
        throw new Error('Invalid response from Edge Function');
      }

      const textContent = data.data;
      if (!textContent || textContent.trim().length === 0) {
        console.error('❌ Empty content in response');
        throw new Error('Empty response from AI service');
      }

      console.log('✅ AI response content received, length:', textContent.length);
      console.log('✅ Provider used:', data.provider);
      console.log('✅ Processing time:', data.processingTime, 'ms');
      return textContent;

    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort errors specifically
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('❌ Request aborted due to timeout');
        throw new Error('AI request timed out');
      }

      console.error('❌ AI API call failed:', error);
      throw error;
    }
  }

  /**
   * Parse AI response into PreReleaseProject format
   */
  private static parsePreReleaseProject(
    response: string,
    originalIdea: BusinessIdea
  ): Partial<PreReleaseProject> {
    try {
      // Extract JSON from response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      // Convert to PreReleaseProject format
      return {
        title: parsed.title || `${originalIdea.title} - Pre-Release`,
        description: parsed.description || `Test and validate ${originalIdea.title} with real users before full development.`,
        concept_details: parsed.concept_details || `This pre-release project validates the core assumptions of ${originalIdea.title}: ${originalIdea.problem}`,
        key_features: Array.isArray(parsed.key_features)
          ? parsed.key_features.map((feature: string) => ({ text: feature }))
          : [
            { text: 'Core value proposition validation' },
            { text: 'Target customer feedback collection' },
            { text: 'Pricing and demand testing' },
            { text: 'User experience validation' },
            { text: 'Market fit assessment' }
          ],
        estimated_timeline: parsed.estimated_timeline || '2-3 weeks',
        proposed_price: typeof parsed.proposed_price === 'number'
          ? parsed.proposed_price
          : this.extractPrice(originalIdea),
        early_bird_discount: typeof parsed.early_bird_discount === 'number'
          ? parsed.early_bird_discount
          : 50,
        project_category: parsed.project_category || this.inferCategory(originalIdea),
        project_type: 'app' as const,
        status: 'concept' as const,
        is_active: true
      };

    } catch (error) {
      console.error('Failed to parse AI response:', error);
      throw new Error('Failed to parse pre-release project from AI response');
    }
  }

  /**
   * Extract or infer pricing from business idea
   */
  private static extractPrice(idea: BusinessIdea): number {
    // Try to extract price from validation question or solution
    const text = `${idea.validationQuestion || ''} ${idea.solution}`.toLowerCase();

    // Look for price patterns
    const priceMatch = text.match(/\$(\d+)/);
    if (priceMatch) {
      return parseInt(priceMatch[1]);
    }

    // Default pricing based on technical feasibility and market
    switch (idea.technicalFeasibility) {
      case 'high': return 19;
      case 'medium': return 39;
      case 'low': return 79;
      default: return 29;
    }
  }

  /**
   * Infer project category from business idea
   */
  private static inferCategory(idea: BusinessIdea): string {
    const text = `${idea.title} ${idea.problem} ${idea.solution}`.toLowerCase();

    if (text.includes('ecommerce') || text.includes('e-commerce') || text.includes('shop') || text.includes('store')) {
      return 'E-commerce';
    }
    if (text.includes('productiv') || text.includes('task') || text.includes('workflow')) {
      return 'Productivity';
    }
    if (text.includes('develop') || text.includes('code') || text.includes('api') || text.includes('tech')) {
      return 'Developer Tools';
    }
    if (text.includes('social') || text.includes('community') || text.includes('network')) {
      return 'Social';
    }
    if (text.includes('market') || text.includes('analyt') || text.includes('data')) {
      return 'Analytics';
    }
    if (text.includes('design') || text.includes('creative') || text.includes('content')) {
      return 'Design';
    }

    return 'Business Tools';
  }

  /**
   * Fallback pre-release project when AI fails
   */
  private static getFallbackPreReleaseProject(
    idea: BusinessIdea,
    market: JourneyMarket,
    persona: JourneyPersona,
    language: string = 'en'
  ): Partial<PreReleaseProject> {
    // Generate idea-specific features instead of generic ones
    const ideaSpecificFeatures = this.generateIdeaSpecificFeatures(idea, market, language);

    // Translations for fallback content
    const translations = {
      en: {
        earlyAccess: 'Early Access Program',
        description: (title: string) => `Test and validate ${title} with real users before full launch. Get early feedback and build a community of engaged users.`,
        conceptDetails: (title: string, customer: string, problem: string, solution: string) => 
          `This pre-release program validates the core value proposition of ${title}. We're testing whether ${customer} actually experience the problem: "${problem}" and if our solution approach - ${solution} - resonates with them. This validation phase will help us refine the product before full launch.`,
      },
      zh: {
        earlyAccess: '早期访问计划',
        description: (title: string) => `在正式发布前与真实用户一起测试和验证${title}。获得早期反馈并建立参与用户社区。`,
        conceptDetails: (title: string, customer: string, problem: string, solution: string) => 
          `此预发布计划验证${title}的核心价值主张。我们正在测试${customer}是否真的遇到了这个问题："${problem}"，以及我们的解决方案 - ${solution} - 是否能引起他们的共鸣。此验证阶段将帮助我们在正式发布前完善产品。`,
      }
    };

    const t = translations[language as 'en' | 'zh'] || translations.en;

    return {
      title: `${idea.title} - ${t.earlyAccess}`,
      description: t.description(idea.title),
      concept_details: t.conceptDetails(idea.title, idea.targetCustomer || idea.targetAudience, idea.problem, idea.solution),
      key_features: ideaSpecificFeatures,
      estimated_timeline: this.getTimelineByFeasibility(idea.technicalFeasibility),
      proposed_price: this.extractPrice(idea),
      early_bird_discount: 50,
      project_category: this.inferCategory(idea),
      project_type: 'app',
      status: 'concept',
      is_active: true
    };
  }

  /**
   * Generate idea-specific features for fallback (actual product features, not problem/solution restatement)
   */
  private static generateIdeaSpecificFeatures(
    idea: BusinessIdea,
    market: JourneyMarket,
    language: string = 'en'
  ): Array<{ text: string }> {
    const features: Array<{ text: string }> = [];

    // Extract key concepts from the solution to generate actual features
    const solutionLower = idea.solution.toLowerCase();
    const problemLower = idea.problem.toLowerCase();
    const skillsLower = idea.skillsRequired.map(s => s.toLowerCase());

    const translations = {
      en: {
        // Core functionality features
        coreFeature: (solution: string) => {
          // Extract the main action/capability from solution
          if (solution.includes('automat')) return 'Automated workflow and task management';
          if (solution.includes('ai') || solution.includes('intelligent')) return 'AI-powered intelligent recommendations';
          if (solution.includes('track') || solution.includes('monitor')) return 'Real-time tracking and monitoring dashboard';
          if (solution.includes('analyz') || solution.includes('insight')) return 'Advanced analytics and insights';
          if (solution.includes('integrat')) return 'Seamless integration with existing tools';
          if (solution.includes('collaborat')) return 'Team collaboration and communication features';
          if (solution.includes('custom')) return 'Customizable settings and preferences';
          if (solution.includes('schedul')) return 'Smart scheduling and planning tools';
          if (solution.includes('notif') || solution.includes('alert')) return 'Intelligent notifications and alerts';
          if (solution.includes('search') || solution.includes('find')) return 'Powerful search and discovery features';
          return 'Core functionality tailored to your needs';
        },
        userExperience: 'Intuitive user interface designed for ease of use',
        mobileAccess: 'Mobile-friendly access from any device',
        dataManagement: 'Secure data storage and management',
        reporting: 'Comprehensive reporting and export capabilities',
        support: 'Priority customer support and onboarding',
        community: 'Access to exclusive beta community',
        earlyBird: 'Special early-bird pricing (50% off)',
        // Market-specific features
        ecommerce: 'Payment processing and transaction management',
        tech: 'API access and developer tools',
        social: 'Social sharing and engagement features',
        // Feasibility-based features
        highFeasibility: 'Rapid deployment and quick setup',
        mediumFeasibility: 'Balanced feature set with proven technology',
        lowFeasibility: 'Innovative approach with cutting-edge technology',
      },
      zh: {
        coreFeature: (solution: string) => {
          if (solution.includes('automat') || solution.includes('自动')) return '自动化工作流程和任务管理';
          if (solution.includes('ai') || solution.includes('intelligent') || solution.includes('智能')) return 'AI 驱动的智能推荐';
          if (solution.includes('track') || solution.includes('monitor') || solution.includes('跟踪') || solution.includes('监控')) return '实时跟踪和监控仪表板';
          if (solution.includes('analyz') || solution.includes('insight') || solution.includes('分析')) return '高级分析和洞察';
          if (solution.includes('integrat') || solution.includes('集成')) return '与现有工具无缝集成';
          if (solution.includes('collaborat') || solution.includes('协作')) return '团队协作和沟通功能';
          if (solution.includes('custom') || solution.includes('定制')) return '可定制的设置和偏好';
          if (solution.includes('schedul') || solution.includes('计划') || solution.includes('安排')) return '智能调度和规划工具';
          if (solution.includes('notif') || solution.includes('alert') || solution.includes('通知')) return '智能通知和提醒';
          if (solution.includes('search') || solution.includes('find') || solution.includes('搜索')) return '强大的搜索和发现功能';
          return '为您的需求量身定制的核心功能';
        },
        userExperience: '直观易用的用户界面设计',
        mobileAccess: '支持任何设备的移动访问',
        dataManagement: '安全的数据存储和管理',
        reporting: '全面的报告和导出功能',
        support: '优先客户支持和入门指导',
        community: '访问专属测试社区',
        earlyBird: '特殊早鸟价格（5折优惠）',
        ecommerce: '支付处理和交易管理',
        tech: 'API 访问和开发者工具',
        social: '社交分享和互动功能',
        highFeasibility: '快速部署和快速设置',
        mediumFeasibility: '平衡的功能集与成熟技术',
        lowFeasibility: '采用前沿技术的创新方法',
      }
    };

    const t = translations[language as 'en' | 'zh'] || translations.en;

    // Feature 1: Core feature derived from solution
    features.push({ text: t.coreFeature(idea.solution) });

    // Feature 2: User experience (always important)
    features.push({ text: t.userExperience });

    // Feature 3: Market-specific feature
    if (market === 'E-commerce') {
      features.push({ text: t.ecommerce });
    } else if (market === 'IT/Tech') {
      features.push({ text: t.tech });
    } else if (market === 'Social Media/Influencer') {
      features.push({ text: t.social });
    } else {
      features.push({ text: t.mobileAccess });
    }

    // Feature 4: Data/reporting (important for validation)
    features.push({ text: t.reporting });

    // Feature 5: Feasibility-based feature
    if (idea.technicalFeasibility === 'high') {
      features.push({ text: t.highFeasibility });
    } else if (idea.technicalFeasibility === 'low') {
      features.push({ text: t.lowFeasibility });
    } else {
      features.push({ text: t.mediumFeasibility });
    }

    // Feature 6: Support (beta program benefit)
    features.push({ text: t.support });

    // Feature 7: Community access (beta program benefit)
    features.push({ text: t.community });

    // Feature 8: Early bird pricing (beta program benefit)
    features.push({ text: t.earlyBird });

    // Return 5-6 most relevant features
    return features.slice(0, 6);
  }



  /**
   * Get persona-specific goals for fallback
   */
  private static getPersonaSpecificGoals(persona: JourneyPersona, idea: BusinessIdea, language: string = 'en'): string[] {
    const translations = {
      en: {
        skilledMaker: [
          'Validate technical feasibility with real users',
          'Test core feature functionality and usability',
          'Gather feedback on technical implementation',
          'Assess market demand for the solution'
        ],
        expertService: [
          'Validate service delivery model with clients',
          'Test pricing and service packages',
          'Build case studies and testimonials',
          'Refine service offering based on feedback'
        ],
        founderPlan: [
          'Validate business model assumptions',
          'Test go-to-market strategy effectiveness',
          'Measure customer acquisition and retention',
          'Optimize conversion funnel and pricing'
        ],
        default: [
          'Validate core value proposition with target users',
          'Test user experience and product-market fit',
          'Gather feedback for product improvement',
          'Build community of early adopters'
        ]
      },
      zh: {
        skilledMaker: [
          '与真实用户验证技术可行性',
          '测试核心功能和可用性',
          '收集技术实施反馈',
          '评估解决方案的市场需求'
        ],
        expertService: [
          '与客户验证服务交付模式',
          '测试定价和服务套餐',
          '建立案例研究和推荐',
          '根据反馈完善服务产品'
        ],
        founderPlan: [
          '验证商业模式假设',
          '测试市场进入策略的有效性',
          '衡量客户获取和留存',
          '优化转化漏斗和定价'
        ],
        default: [
          '与目标用户验证核心价值主张',
          '测试用户体验和产品市场契合度',
          '收集产品改进反馈',
          '建立早期采用者社区'
        ]
      }
    };

    const t = translations[language as 'en' | 'zh'] || translations.en;

    switch (persona) {
      case 'skilled-maker':
        return t.skilledMaker;
      case 'expert-service':
        return t.expertService;
      case 'founder-plan':
        return t.founderPlan;
      default:
        return t.default;
    }
  }

  /**
   * Get timeline based on technical feasibility
   */
  private static getTimelineByFeasibility(feasibility: string): string {
    switch (feasibility) {
      case 'high': return '1-2 weeks';
      case 'medium': return '2-3 weeks';
      case 'low': return '3-4 weeks';
      default: return '2-3 weeks';
    }
  }

  /**
   * Get language name from language code
   */
  private static getLanguageName(languageCode: string): string {
    const languageMap: Record<string, string> = {
      'en': 'English',
      'zh': 'Chinese (中文)',
      'es': 'Spanish (Español)',
      'fr': 'French (Français)',
      'de': 'German (Deutsch)',
      'ja': 'Japanese (日本語)',
      'ko': 'Korean (한국어)',
      'pt': 'Portuguese (Português)',
      'ru': 'Russian (Русский)',
      'ar': 'Arabic (العربية)',
      'hi': 'Hindi (हिन्दी)',
      'it': 'Italian (Italiano)'
    };
    return languageMap[languageCode] || 'English';
  }

  /**
   * Generate beta program assets (landing page copy, email templates, etc.)
   */
  static async generateBetaProgramAssets(
    preReleaseProject: Partial<PreReleaseProject>,
    selectedIdea: BusinessIdea,
    language: string = 'en'
  ): Promise<{
    landingPageCopy: string;
    emailTemplates: { welcome: string; feedback: string; launch: string };
    socialMediaPosts: string[];
    surveyQuestions: string[];
  }> {
    try {
      const prompt = this.createBetaAssetsPrompt(preReleaseProject, selectedIdea, language);
      const response = await this.callAI(prompt);
      return this.parseBetaAssets(response);
    } catch (error) {
      console.error('AI beta assets generation failed, using fallback:', error);
      return this.getFallbackBetaAssets(preReleaseProject, selectedIdea);
    }
  }

  /**
   * Create prompt for beta program assets
   */
  private static createBetaAssetsPrompt(
    project: Partial<PreReleaseProject>,
    idea: BusinessIdea,
    language: string = 'en'
  ): string {
    const languageInstruction = language !== 'en' 
      ? `\n\nIMPORTANT: Generate ALL content in ${this.getLanguageName(language)} language. All text including headlines, emails, posts, and questions must be in ${this.getLanguageName(language)}.`
      : '';
    
    return `Generate comprehensive beta program assets for this pre-release project:${languageInstruction}

Project: ${project.title}
Description: ${project.description}
Target Customer: ${idea.targetCustomer || idea.targetAudience}
Problem: ${idea.problem}
Solution: ${idea.solution}

Generate the following assets:

1. Landing Page Copy (compelling headline, benefits, CTA)
2. Email Templates (welcome, feedback request, launch announcement)
3. Social Media Posts (3 different posts for promotion)
4. Survey Questions (5 questions to validate the concept)

Format as JSON:
{
  "landingPageCopy": {
    "headline": "Compelling headline that hooks the target customer",
    "subheadline": "Supporting text that explains the value",
    "benefits": ["Benefit 1", "Benefit 2", "Benefit 3"],
    "cta": "Clear call-to-action text",
    "socialProof": "Text about early supporters or validation"
  },
  "emailTemplates": {
    "welcome": "Welcome email for new beta signups",
    "feedback": "Email requesting feedback after trial period", 
    "launch": "Launch announcement for beta participants"
  },
  "socialMediaPosts": [
    "Post 1: Problem-focused post",
    "Post 2: Solution-focused post", 
    "Post 3: Community/beta invitation post"
  ],
  "surveyQuestions": [
    "Question 1: Problem validation",
    "Question 2: Solution fit",
    "Question 3: Pricing sensitivity",
    "Question 4: Feature priorities",
    "Question 5: Likelihood to recommend"
  ]
}

Make it specific, actionable, and compelling for the target audience.

Return only the JSON object.`;
  }

  /**
   * Parse beta assets from AI response
   */
  private static parseBetaAssets(response: string): {
    landingPageCopy: string;
    emailTemplates: { welcome: string; feedback: string; launch: string };
    socialMediaPosts: string[];
    surveyQuestions: string[];
  } {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      // Format landing page copy
      const landingPage = parsed.landingPageCopy || {};
      const landingPageCopy = `
# ${landingPage.headline || 'Join Our Beta Program'}

${landingPage.subheadline || 'Be among the first to try our innovative solution'}

## Benefits:
${(landingPage.benefits || []).map((benefit: string) => `• ${benefit}`).join('\n')}

${landingPage.socialProof || 'Join other early adopters who are already seeing results'}

**${landingPage.cta || 'Sign Up for Early Access'}**
      `.trim();

      return {
        landingPageCopy,
        emailTemplates: {
          welcome: parsed.emailTemplates?.welcome || 'Welcome to our beta program! We\'re excited to have you on board.',
          feedback: parsed.emailTemplates?.feedback || 'How has your experience been so far? We\'d love your feedback.',
          launch: parsed.emailTemplates?.launch || 'We\'re officially launching! Thank you for being part of our beta.'
        },
        socialMediaPosts: parsed.socialMediaPosts || [
          'Solving a problem that affects thousands of people daily...',
          'What if there was a better way to handle this common challenge?',
          'Join our beta program and help shape the future of this solution!'
        ],
        surveyQuestions: parsed.surveyQuestions || [
          'How often do you experience this problem?',
          'How well does our solution address your needs?',
          'What would you be willing to pay for this solution?',
          'What features are most important to you?',
          'How likely are you to recommend this to others?'
        ]
      };

    } catch (error) {
      console.error('Failed to parse beta assets:', error);
      throw new Error('Failed to parse beta assets from AI response');
    }
  }

  /**
   * Fallback beta assets when AI fails
   */
  private static getFallbackBetaAssets(
    project: Partial<PreReleaseProject>,
    idea: BusinessIdea
  ): {
    landingPageCopy: string;
    emailTemplates: { welcome: string; feedback: string; launch: string };
    socialMediaPosts: string[];
    surveyQuestions: string[];
  } {
    const landingPageCopy = `
# ${project.title} - Early Access

Get exclusive early access to ${idea.title} and help shape its development.

## Why Join Our Beta?
• Be the first to solve ${idea.problem}
• Get special early-bird pricing
• Direct influence on product features
• Priority support from our team
• Join a community of early adopters

**Sign Up for Beta Access**
    `.trim();

    return {
      landingPageCopy,
      emailTemplates: {
        welcome: `Welcome to the ${project.title} beta program! We're thrilled to have you join us in testing and improving ${idea.title}. You'll receive access details shortly.`,
        feedback: `Hi! You've been using ${idea.title} for a while now. We'd love to hear about your experience and how we can make it even better for you.`,
        launch: `Big news! ${idea.title} is officially launching. As a beta participant, you get exclusive early access and special pricing. Thank you for helping us get here!`
      },
      socialMediaPosts: [
        `Tired of ${idea.problem}? We're building a solution and need beta testers. Join us! #startup #beta`,
        `What if ${idea.solution}? We're making it happen. Looking for early users to test and provide feedback.`,
        `Join our exclusive beta program for ${idea.title}. Help us build something amazing together! 🚀`
      ],
      surveyQuestions: [
        `How often do you experience this problem: ${idea.problem}?`,
        `How well does ${idea.title} solve your problem? (1-10 scale)`,
        `What would you be willing to pay monthly for this solution?`,
        `What's the most important feature for you?`,
        `How likely are you to recommend ${idea.title} to others?`
      ]
    };
  }
}